import * as React from 'react';
import { StyleSheet, View } from 'react-native';
import { useHistory, useLocation } from 'react-router-dom';
import { signIn } from 'aws-amplify/auth';
import { useApolloClient } from '@apollo/react-hooks';

import { getLocalizedTexts } from '../../Locales';
import color from '../../style/color';
import { useWindowSize } from '../../style/utils';
import { getSocialIdentityCredentials, GetSocialIdentityCredentialsResponse, Provider } from '../../api/rest/user';
import { assertSignInCompleted } from '../../lib/auth/signIn';
import {
    exchangeGoogleCodeForIdToken,
    GoogleIdentityDocument,
    verifyAndDecodeGoogleJsonWebToken,
    getSocialIdentityDocument,
    GOOGLE_OAUTH_CLIENT_ID,
} from '../../lib/auth/googleLogin';
import { logUserEventBestEffort, queueUserEventForReplay } from '../../lib/events/userEvents';
import IntroPanel from './IntroPanel';
import LoadingFrame from '../../components/auth/LoadingFrame';
import SocialSignUpPage from './SocialSignUpPage';
import {
    OAUTH_PROVIDERS,
    beginOAuthRequest,
    consumeOAuthRequest,
    getOAuthCallbackResult,
    isOAuthRequestSecurityError,
} from '../../lib/auth/oauthRequestSecurity';
import { captureSentryException } from '../../lib/common/sentry';

const LOCALHOST_REDIRECT_URL = 'http://localhost:3000/auth/sign-up/google';

function GoogleAuthPage() {
    const texts = getLocalizedTexts().auth.signIn.withGoogle;
    const size = useWindowSize();
    const [signUpData, errorMessage] = useHandleRedirection();
    if (!signUpData)
        return (
            <View style={styles.container}>
                <LoadingFrame
                    text={errorMessage || texts.loading}
                    height={(size.height || 0) - 120}
                    width={(size.width || 0) - 240}
                    isError={!!errorMessage}
                />
            </View>
        );
    return (
        <View style={styles.containerSignUp}>
            <View style={styles.containerSignUpColumn}>
                <IntroPanel />
            </View>
            <View style={styles.containerSignUpColumn}>
                <SocialSignUpPage {...signUpData} />
            </View>
        </View>
    );
}

export default GoogleAuthPage;

function useHandleRedirection(): [SignUpData | undefined, string | undefined] {
    const location = useLocation();
    const redirectUrl =
        window.location.host === 'localhost:3000'
            ? LOCALHOST_REDIRECT_URL
            : `https://${window.location.host}${location.pathname}`; // When testing locally, we redirect to localhost with http instead of https to avoid a `ERR_SSL_PROTOCOL_ERROR` error.
    const { handleOAuthResponse, handleOAuthFailure, signUpData, errorMessage } = useHandleOAuthResponse(redirectUrl);
    React.useEffect(() => {
        handleRedirection(handleOAuthResponse, location.search, redirectUrl).catch(handleOAuthFailure);
    }, []); // eslint-disable-line react-hooks/exhaustive-deps
    return [signUpData, errorMessage];
}

async function handleRedirection(
    handleOAuthResponse: (code: string, identityDocument: GoogleIdentityDocument, returnedState: string | null) => void,
    locationUrlQuery: string,
    redirectUrl: string
) {
    const urlSearchParams = new URLSearchParams(locationUrlQuery);
    const callback = getOAuthCallbackResult({
        provider: OAUTH_PROVIDERS.GOOGLE,
        responseParameters: urlSearchParams,
        requiresNonce: true,
        successParameter: 'code',
    });
    if (callback.type === 'failure') throw callback.error;

    const identityDocument = await getSocialIdentityDocument();
    if (callback.type === 'success') {
        handleOAuthResponse(callback.value, identityDocument, callback.returnedState);
        return;
    }

    // We open a window on the authorization endpoint to open the google sign in modal.
    // Once the user accepts to sign in, google will redirect to `redirect_uri` with `code` as a url parameter.
    const request = beginOAuthRequest(OAUTH_PROVIDERS.GOOGLE, true);
    if (request.nonce === undefined) throw new Error('Google OAuth nonce missing');
    const authorizationUrl = new URL(identityDocument.authorization_endpoint);
    authorizationUrl.searchParams.set('client_id', GOOGLE_OAUTH_CLIENT_ID);
    authorizationUrl.searchParams.set('response_type', 'code');
    authorizationUrl.searchParams.set('redirect_uri', redirectUrl);
    authorizationUrl.searchParams.set('state', request.state);
    authorizationUrl.searchParams.set('nonce', request.nonce);
    authorizationUrl.searchParams.set('scope', 'openid profile email');
    window.open(authorizationUrl.toString(), '_self');
}

interface SignUpData {
    initialEmail: string;
    credentials: GetSocialIdentityCredentialsResponse;
}

function useHandleOAuthResponse(redirectUrl: string): {
    handleOAuthResponse: (code: string, identityDocument: GoogleIdentityDocument, returnedState: string | null) => void;
    handleOAuthFailure: (error: unknown) => void;
    signUpData: SignUpData | undefined;
    errorMessage: string | undefined;
} {
    const texts = getLocalizedTexts().auth.signIn.withGoogle;
    const history = useHistory();
    const apolloClient = useApolloClient();
    const [errorMessage, setErrorMessage] = React.useState<string | undefined>(undefined);
    const [signUpData, setSignUpData] = React.useState<SignUpData | undefined>(undefined);
    const handleOAuthFailure = (error: unknown) => {
        console.log(error);
        if (isOAuthRequestSecurityError(error))
            captureSentryException(error, {
                oauthFailureReason: error.reason,
                oauthProvider: error.provider,
            });
        setErrorMessage(texts.error.default);
        logUserEventBestEffort(apolloClient, { type: 'googleLoginFailed' });
        setTimeout(() => history.push('/auth'), 1000);
    };
    const handleOAuthResponse = async (
        code: string,
        identityDocument: GoogleIdentityDocument,
        returnedState: string | null
    ) => {
        try {
            const { expectedNonce } = consumeOAuthRequest(OAUTH_PROVIDERS.GOOGLE, returnedState, true);
            if (expectedNonce === undefined) throw new Error('Google OAuth nonce missing');
            const idJsonWebToken = await exchangeGoogleCodeForIdToken(code, redirectUrl);
            const googleIdToken = await verifyAndDecodeGoogleJsonWebToken(
                idJsonWebToken,
                identityDocument,
                expectedNonce
            );
            const userEmail = googleIdToken.email;
            if (!userEmail) throw new Error('No email was received in the Id token returned by user-appsync-api');
            const credentials = await getSocialIdentityCredentials({
                provider: Provider.Google,
                providerUserId: googleIdToken.sub,
                email: userEmail,
                secretToken: idJsonWebToken,
            });
            if (!credentials.new) await handleSignIn(credentials);
            else setSignUpData({ initialEmail: userEmail, credentials });
        } catch (error) {
            handleOAuthFailure(error);
        }
    };
    return { handleOAuthResponse, handleOAuthFailure, signUpData, errorMessage };
}

async function handleSignIn(credentials: GetSocialIdentityCredentialsResponse) {
    const authResponse = await signIn({
        username: credentials.userId,
        password: credentials.secretKey,
        options: { authFlowType: 'CUSTOM_WITH_SRP' },
    });
    assertSignInCompleted(authResponse);
    queueUserEventForReplay({ type: 'googleLoginSucceeded' });
    window.location.reload();
}

const styles = StyleSheet.create({
    container: {
        flex: 1,
        marginTop: 60,
        flexDirection: 'column',
        alignItems: 'center',
        backgroundColor: color.white,
    },
    containerSignUp: {
        flex: 1,
        flexDirection: 'row',
    },
    containerSignUpColumn: {
        flex: 1,
    },
});
